'use client';
import { useEffect, useState, useRef } from 'react';
import Slider from 'react-slick';
import LazyLoadImage from '@repo/ui/src/components/LazyLoadImage';
import Product from '@models/product';
import { RightArrow, LeftArrow } from '@repo/ui/src/components/Carousels/CarouselArrows';
import ProductBundleInfo, { ChildrenProducts } from '@views/ProductBundlePage/_ProductBundleInfo';
import useWindowSize from '@repo/ui/src/components/shared/window-size';
import createCurrencyString from '@repo/ui/src/functions/currency-functions';
import CallToActionButton from '@components/Product/CallToActionButton';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import useAddToBasket from '@hooks/basket/useAddToBasket';
import { shopDataProductsFetcher } from '@hooks/product/useShopDataProducts';
import ProductCarouselItem from './_ProductCarouselItem';
import LoadingLogo from '@components/Loading/LoadingLogo';

const getProductFromRoute = (query, path) => {
  const id = query?.get('id');
  if (!id?.length) return null;
  const searchString = path.substring(id.length + 2);
  const params = searchString.split('=');
  return params?.length === 2 ? params[1] : null;
};

const ProductBundlePage = ({ bundles, title, description, header, backgroundImage }) => {
  const { currency, locale, query, path } = useCurrentUrl();
  const { trigger } = useAddToBasket();
  const initialSku = getProductFromRoute(query, path);
  const [activeParent, setActiveParent] = useState(null);

  const [parentProducts, setParentProducts] = useState([]);
  const [childrenProducts, setChildrenProducts] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(true);
  const [bundleVisible, setBundleVisible] = useState(false);
  const [activeParentInStock, setActiveParentInStock] = useState(null);
  const [sum, setSum] = useState(0);
  const [width] = useWindowSize();
  const isSmall = width < 768;
  const currentBundle = bundles?.find((item) => item.parent === activeParent?.sku);
  const currentBundleImg = (activeParent ?? new Product()).getMainImageSize(
    isSmall ? 180 : 300,
    1,
    1,
  );

  const slider1 = useRef();

  const showAll = parentProducts?.length <= 3;

  const settings = {
    focusOnSelect: true,
    infinite: true,
    slidesToShow: showAll ? parentProducts?.length : 5,
    slidesToScroll: 1,
    swipeToSlide: true,
    centerMode: true,
    easing: 'ease-in',
    speed: 1000,
    nextArrow: <RightArrow />,
    prevArrow: <LeftArrow />,
    className: 'slider variable-width',
    afterChange: (current) => {
      setActiveParent(parentProducts[current]);
    },
    onReInit: () => {
      if (!activeParent && parentProducts?.length > 0 && slider1?.current?.slickGoTo) {
        let productIndex = initialSku ? parentProducts.findIndex((x) => x.sku === initialSku) : 0;
        if (productIndex < 0) productIndex = 0;
        slider1?.current?.slickGoTo(productIndex);
        const { childrenSkus } = parentProducts[productIndex];
        setActiveParent(parentProducts[productIndex]);
        getAndSetChildrenContent(childrenSkus);
      }
    },
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: showAll ? parentProducts.length : 3,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  const getAndSetParentContent = async (skus) => {
    try {
      const content = await shopDataProductsFetcher(skus, locale);
      if (content?.length > 0) {
        const newProducts = content.map((item) => new Product({ shop: item }));
        const bundleOrder = bundles.map((item) => item.parent);
        const ordered = newProducts.sort((a, b) =>
          bundleOrder.indexOf(a?.sku) > bundleOrder.indexOf(b?.sku)
            ? 1
            : bundleOrder.indexOf(a?.sku) === bundleOrder.indexOf(b?.sku)
              ? bundleOrder.indexOf(a?.sku) > bundleOrder.indexOf(b?.sku)
                ? 1
                : -1
              : -1,
        );
        setParentProducts(ordered);
      }
    } catch (error) {
      console.error('Get parent error ', error);
    }
  };

  const getAndSetChildrenContent = async (childrenSkus) => {
    try {
      if (childrenSkus?.length > 0) {
        const content = await shopDataProductsFetcher(childrenSkus, locale);
        if (content?.length > 0) {
          const newProducts = content.map((item) => new Product({ shop: item }));
          const newArr = childrenProducts.filter((item) =>
            newProducts.find((prod) => prod?.sku === item?.sku),
          );
          newProducts.forEach((item) => {
            const found = newArr.find((prod) => item?.sku === prod?.sku);
            const itemInStock = item.isOrderable;
            if (!found && itemInStock) {
              newArr.push(item);
            }
          });
          const getPimData = async () => {
            const data = await shopDataProductsFetcher(childrenSkus, locale);

            if (data.length) {
              const dataSkus = data.map((item) => item.id);
              setChildrenProducts(newArr.filter((item) => dataSkus.includes(item?.sku)));
              setLoadingChildren(false);
            }
          };
          await getPimData(newArr);
        }
      }
    } catch (error) {
      console.error('Get parent error ', error);
    }
  };

  useEffect(() => {
    let currentPrice = 0;
    if (childrenProducts?.length > 0) {
      childrenProducts.forEach((item) => {
        if (item.price && item.isOrderable) {
          currentPrice += parseFloat(item.price);
        }
      });

      currentPrice += parseFloat(activeParent.price);
      setSum(currentPrice.toFixed(2));
    }
  }, [childrenProducts]);

  useEffect(() => {
    if (activeParent?.sku) {
      const { childrenSkus } = bundles.find((item) => item.parent === activeParent?.sku);
      setLoadingChildren(true);
      getAndSetChildrenContent(childrenSkus);
    }
  }, [activeParent]);

  useEffect(() => {
    const bundleParents = bundles?.map((item) => item.parent) || [];
    if (bundleParents.length > 0) {
      getAndSetParentContent(bundleParents);
    }
  }, []);

  if (!parentProducts.length) return null;

  return (
    <>
      <div className="banner-background tw-px-0">
        <div className="tw-container tw-flex tw-flex-col tw-justify-center tw-text-center tw-px-5 md:tw-px-0 tw-pt-20 tw-max-paragraph tw-m-auto">
          {title && <h1 className={`${backgroundImage ? 'color-white' : ''}`}>{title}</h1>}
          {description && (
            <div className="tw-py-4">
              <p className={`${backgroundImage ? 'color-white' : ''}`}>{description}</p>
            </div>
          )}
          {header && <h3 className={`${backgroundImage ? 'color-white' : ''}`}>{header}</h3>}
        </div>
        <div className="product-bundle-carousel tw-pt-0 tw-pb-0">
          <Slider {...settings} ref={(slider) => (slider1.current = slider)}>
            {parentProducts.map((item) => (
              <ProductCarouselItem
                key={`Product_Bundle_${item.title}`}
                product={item}
                bundleVisible={bundleVisible}
                setBundleVisible={setBundleVisible}
                activeParentInStock={activeParentInStock}
                setActiveParentInStock={setActiveParentInStock}
                activeParent={activeParent}
                locale={locale}
                currency={currency}
              />
            ))}
          </Slider>
        </div>
      </div>
      <div>
        {loadingChildren ? (
          <div className="tw-py-6 tw-text-center">
            <div className="tw-hidden" />
            <LoadingLogo />
            <h5 className="tw-mt-4">...loading bundle</h5>
          </div>
        ) : bundleVisible ? (
          <div>
            <hr className="tw-hidden md:tw-block" />
            <div className=" md:tw-hidden tw-grid tw-grid-cols-2">
              <a
                href={activeParent?.productUrl}
                className="tw-underline tw-cursor-pointer"
                target="_blank"
                rel="noreferrer"
              >
                <LazyLoadImage
                  imgComponent={<img src={currentBundleImg} alt={currentBundle?.title} />}
                />
              </a>
              <div className="">
                <div className="tw-grid tw-grid-cols-3">
                  <ChildrenProducts
                    {...{
                      bundles,
                      childrenProducts,
                      activeParent,
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="tw-flex tw-items-center tw-justify-center tw-flex-col md:tw-grid md:tw-grid-cols-[auto_2fr_1fr] tw-max-w-[1200px] tw-m-auto tw-gap-5 tw-py-20">
              <div>
                {currentBundle.title && (
                  <h2 className="tra-text-web-heading-4">{currentBundle.title}</h2>
                )}
              </div>
              <div
                className={`${
                  activeParent?.ctaButtonType !== 'noCTA' ? 'col-lg-5' : 'col-lg-8'
                } tw-flex tw-flex-col tw-justify-center tw-items-center md:tw-grid md:tw-justify-items-end`}
              >
                {sum && (
                  <>
                    <div>
                      <strong>{createCurrencyString(locale, currency, sum)}</strong>
                    </div>
                  </>
                )}
              </div>
              {activeParent?.ctaButtonType !== 'noCTA' && (
                <div className="tw-flex tw-align-middle tw-max-w-[400px]">
                  <CallToActionButton
                    buttonProps={{ full: true }}
                    onClickAddToCart={() => {
                      activeParent.quantity = 1;
                      const bundleProducts = childrenProducts.map((product) => {
                        product.quantity = 1;
                        return product;
                      });
                      return trigger([activeParent, ...bundleProducts]);
                    }}
                    skus={[activeParent.sku]}
                    product={activeParent}
                    customProductPrice={sum}
                  />
                </div>
              )}
            </div>
            <hr />
          </div>
        ) : (
          <>
            <hr />
            <div className="tw-flex tw-justify-center tw-w-full tw-py-10">
              <h2>BUNDLE NOT IN STOCK</h2>
            </div>
            <hr />
          </>
        )}
      </div>
      {loadingChildren ? (
        <div>
          <div />
        </div>
      ) : bundleVisible ? (
        <ProductBundleInfo
          {...{
            bundles,
            childrenProducts,
            activeParent,
          }}
        />
      ) : null}
    </>
  );
};

export default ProductBundlePage;
